var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { HawkinsPropsOverrideContext, useClasses, useConsoleLog, useLayoutProps, useProps, useSkeletonLoader, } from '@hawkins/helpers';
import { IconClose } from '@hawkins/icons/lib/iconClose';
import { HawkinsGlobalClass, useTheme } from '@hawkins/styles';
import { sizings } from '@hawkins/variables';
import { Button } from '../button';
import { BASE_CHIP_HEIGHT, COMPACT_CHIP_HEIGHT, useChipStyles } from './chip.styles';
import { ChipTestIds } from './chip.testids';
var displayName = 'Chip';
/**
 * A chip is a representation of a selected input or attribute.
 */
export var Chip = React.forwardRef(function (props, ref) {
    var _a, _b;
    // Props
    var _c = useProps(displayName, props), children = _c.children, classesProp = _c.classes, className = _c.className, _d = _c.compact, compact = _d === void 0 ? false : _d, _e = _c.component, Component = _e === void 0 ? 'span' : _e, endAdornmentProp = _c.endAdornment, _f = _c.deleteButtonProps, deleteButtonProps = _f === void 0 ? {} : _f, layout = _c.layout, loaderProps = _c.loaderProps, loading = _c.loading, onDelete = _c.onDelete, startAdornment = _c.startAdornment, styleProp = _c.style, restProps = __rest(_c, ["children", "classes", "className", "compact", "component", "endAdornment", "deleteButtonProps", "layout", "loaderProps", "loading", "onDelete", "startAdornment", "style"]);
    // Warn user for using `endAdornment` and `onDelete` together
    useConsoleLog(!!endAdornmentProp && !!onDelete, 'Passing `onDelete` with `endAdornment` will override the `endAdornment` to be a delete button.');
    // State
    var hawkinsVariables = useTheme().hawkinsVariables;
    // Override props for children
    // All icons rendered inside of the Chip should be `size="small"`
    var overrideProps = React.useMemo(function () {
        var props = {
            Icon: { size: 'small', style: compact ? { height: 12, width: 12 } : undefined },
            ThirdPartyIcon: { size: 'small', style: compact ? { height: 12, width: 12 } : undefined },
            Avatar: { variant: 'chip' },
        };
        if (compact) {
            props.Avatar.size = 'compact';
        }
        return props;
    }, [compact]);
    // Styles
    var baseClasses = useChipStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var endAdornment = onDelete ? (_jsx(Button, __assign({ "aria-label": "Delete", variant: "icon", compact: true, icon: IconClose, iconProps: { size: 'small' }, style: compact ? { height: sizings.size1, width: sizings.size1 } : {}, onClick: onDelete }, deleteButtonProps))) : (endAdornmentProp);
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, className, (_a = {},
        _a[classes.compact] = compact,
        _a[classes.withStartAdornment] = !!startAdornment && !endAdornment,
        _a[classes.withEndAdornment] = !startAdornment && !!endAdornment,
        _a[classes.withStartAndEndAdornment] = !!startAdornment && !!endAdornment,
        _a));
    var labelClassName = clsx(classes.label, HawkinsGlobalClass.TRUNCATE, (_b = {},
        _b[classes.labelWithStartAdornment] = !!startAdornment && !endAdornment,
        _b[classes.labelWithEndAdornment] = !startAdornment && !!endAdornment,
        _b[classes.labelWithStartAndEndAdornment] = !!startAdornment && !!endAdornment,
        _b));
    var startAdornmentClassName = clsx(classes.startAdornment);
    var endAdornmentClassName = clsx(classes.endAdornment);
    // Content
    var skeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ height: compact ? hawkinsVariables.sizings[COMPACT_CHIP_HEIGHT] : hawkinsVariables.sizings[BASE_CHIP_HEIGHT], variant: 'square', width: 111 }, loaderProps),
        loading: loading,
        style: style,
    });
    if (skeletonLoaderNode)
        return skeletonLoaderNode;
    return (_jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: overrideProps }, { children: _jsxs(Component, __assign({ "data-testid": ChipTestIds.ROOT, className: rootClassName, style: style, ref: ref }, restProps, { children: [!!startAdornment && (_jsx("span", __assign({ "data-testid": ChipTestIds.START_ADORNMENT, className: startAdornmentClassName }, { children: startAdornment }))), _jsx("span", __assign({ "data-testid": ChipTestIds.LABEL, className: labelClassName }, { children: children })), !!endAdornment && (_jsx("span", __assign({ "data-testid": ChipTestIds.END_ADORNMENT, className: endAdornmentClassName }, { children: endAdornment })))] })) })));
});
Chip.displayName = displayName;
